import {
	Badge,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Skeleton,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@ancatag/at-design";
import { useEffect, useState } from "react";
import {
	getModalityLabel,
	listProviderTemplates,
	type Modality,
	type ProviderTemplate,
} from "../services/provider-templates";

// Modality badge colors
const modalityColors: Record<Modality, string> = {
	text: "bg-blue-900 text-blue-300",
	image: "bg-purple-900 text-purple-300",
	audio: "bg-green-900 text-green-300",
	video: "bg-orange-900 text-orange-300",
	embedding: "bg-gray-700 text-gray-300",
};

function ProviderCard({ provider }: { provider: ProviderTemplate }) {
	return (
		<Card className="hover:shadow-md transition-shadow">
			<CardHeader className="pb-3">
				<div className="flex items-start gap-4">
					{provider.logoUrl && (
						<img
							src={provider.logoUrl}
							alt={`${provider.name} logo`}
							className="w-12 h-12 rounded-lg object-contain bg-muted p-1"
							onError={(e) => {
								(e.target as HTMLImageElement).style.display = "none";
							}}
						/>
					)}
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-2">
							<CardTitle className="text-lg">{provider.name}</CardTitle>
							{provider.isHostedAvailable && (
								<Badge variant="secondary" className="text-xs">
									Hosted Available
								</Badge>
							)}
						</div>
						<CardDescription className="mt-1 line-clamp-2">
							{provider.description}
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				{/* Supported Modalities */}
				<div className="mb-4">
					<p className="text-xs font-medium text-muted-foreground mb-2">
						Supported Modalities
					</p>
					<div className="flex flex-wrap gap-1.5">
						{provider.supportedModalities.map((modality) => (
							<span
								key={modality}
								className={`text-xs px-2 py-0.5 rounded-full font-medium ${modalityColors[modality]}`}
							>
								{getModalityLabel(modality)}
							</span>
						))}
					</div>
				</div>

				{/* Default Models Preview */}
				{provider.defaultModels && provider.defaultModels.length > 0 && (
					<div className="mb-4">
						<p className="text-xs font-medium text-muted-foreground mb-2">
							Available Models ({provider.defaultModels.length})
						</p>
						<div className="flex flex-wrap gap-1">
							{provider.defaultModels.slice(0, 5).map((model) => (
								<Badge key={model.id} variant="outline" className="text-xs">
									{model.name}
								</Badge>
							))}
							{provider.defaultModels.length > 5 && (
								<Badge variant="outline" className="text-xs">
									+{provider.defaultModels.length - 5} more
								</Badge>
							)}
						</div>
					</div>
				)}

				{/* Features */}
				{provider.features && (
					<div>
						<p className="text-xs font-medium text-muted-foreground mb-2">
							Features
						</p>
						<div className="flex flex-wrap gap-1">
							{provider.features.streaming && (
								<Badge variant="outline" className="text-xs">
									Streaming
								</Badge>
							)}
							{provider.features.functionCalling && (
								<Badge variant="outline" className="text-xs">
									Function Calling
								</Badge>
							)}
							{provider.features.vision && (
								<Badge variant="outline" className="text-xs">
									Vision
								</Badge>
							)}
							{provider.features.embeddings && (
								<Badge variant="outline" className="text-xs">
									Embeddings
								</Badge>
							)}
							{provider.features.localDeployment && (
								<Badge variant="outline" className="text-xs">
									Local
								</Badge>
							)}
						</div>
					</div>
				)}

				{/* Hosted Pricing */}
				{provider.isHostedAvailable && provider.hostedPricing && (
					<div className="mt-4 pt-4 border-t">
						<p className="text-xs font-medium text-muted-foreground mb-1">
							Hosted Pricing
						</p>
						<p className="text-sm text-foreground">
							${provider.hostedPricing.inputPer1M}/1M input • $
							{provider.hostedPricing.outputPer1M}/1M output
						</p>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function ProviderSkeleton() {
	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-start gap-4">
					<Skeleton className="w-12 h-12 rounded-lg" />
					<div className="flex-1">
						<Skeleton className="h-5 w-32 mb-2" />
						<Skeleton className="h-4 w-full" />
					</div>
				</div>
			</CardHeader>
			<CardContent>
				<Skeleton className="h-4 w-24 mb-2" />
				<div className="flex gap-1.5 mb-4">
					<Skeleton className="h-5 w-16 rounded-full" />
					<Skeleton className="h-5 w-20 rounded-full" />
				</div>
				<Skeleton className="h-4 w-20 mb-2" />
				<div className="flex gap-1">
					<Skeleton className="h-5 w-16" />
					<Skeleton className="h-5 w-20" />
					<Skeleton className="h-5 w-14" />
				</div>
			</CardContent>
		</Card>
	);
}

export function Providers() {
	const [providers, setProviders] = useState<ProviderTemplate[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<"all" | Modality>("all");

	useEffect(() => {
		async function fetchProviders() {
			try {
				setLoading(true);
				setError(null);
				const data = await listProviderTemplates({ isActive: true });
				setProviders(data);
			} catch (err) {
				setError(
					err instanceof Error ? err.message : "Failed to load providers",
				);
			} finally {
				setLoading(false);
			}
		}
		fetchProviders();
	}, []);

	// Filter providers by modality
	const filteredProviders =
		activeTab === "all"
			? providers
			: providers.filter((p) => p.supportedModalities.includes(activeTab));

	return (
		<div>
			<div className="mb-6">
				<h1 className="text-3xl font-bold text-foreground">Providers</h1>
				<p className="text-muted-foreground mt-2">
					Browse available AI providers and their capabilities. Create a Model
					Config to start using them.
				</p>
			</div>

			{error && (
				<div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
					{error}
				</div>
			)}

			<Tabs
				value={activeTab}
				onValueChange={(v) => setActiveTab(v as typeof activeTab)}
			>
				<TabsList className="mb-6">
					<TabsTrigger value="all">All Providers</TabsTrigger>
					<TabsTrigger value="text">Text</TabsTrigger>
					<TabsTrigger value="image">Image</TabsTrigger>
					<TabsTrigger value="audio">Audio</TabsTrigger>
					<TabsTrigger value="video">Video</TabsTrigger>
					<TabsTrigger value="embedding">Embeddings</TabsTrigger>
				</TabsList>

				<TabsContent value={activeTab} className="mt-0">
					{loading ? (
						<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
							{[...Array(6)].map((_, i) => (
								<ProviderSkeleton key={i} />
							))}
						</div>
					) : filteredProviders.length === 0 ? (
						<div className="p-8 border-2 border-dashed border-border rounded-lg text-center">
							<p className="text-muted-foreground">
								No providers found for this category.
							</p>
						</div>
					) : (
						<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
							{filteredProviders.map((provider) => (
								<ProviderCard key={provider.id} provider={provider} />
							))}
						</div>
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
